import React, { useState, useRef } from "react";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Send } from "lucide-react";

export function highlightMentions(text, names = []) {
  if (!names || !names.length) return text;
  const esc = names.map((n) => n.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"));
  const parts = String(text).split(new RegExp(`(@(?:${esc.join("|")}))`, "g"));
  const set = new Set(names.map((n) => "@" + n));
  return parts.map((p, i) => set.has(p)
    ? <span key={i} className="text-[#5b2fd6] font-medium bg-[#EDE7FF] rounded px-1">{p}</span>
    : <span key={i}>{p}</span>);
}

// Multi-line comment box: Enter = newline, Send button submits, "@" opens teammate tagging.
export function MentionComposer({ users = [], disabled, placeholder, onSend, testidPrefix = "comment" }) {
  const [text, setText] = useState("");
  const [mention, setMention] = useState(null);
  const [mentioned, setMentioned] = useState([]);
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);

  const onChange = (e) => {
    const v = e.target.value; setText(v);
    const caret = e.target.selectionStart ?? v.length;
    const m = v.slice(0, caret).match(/(?:^|\s)@([^\s@]*)$/);
    if (m) setMention({ query: m[1], start: caret - m[1].length - 1, caret }); else setMention(null);
  };
  const pick = (u) => {
    if (!mention) return;
    const before = text.slice(0, mention.start);
    const after = text.slice(mention.caret);
    const insert = `@${u.name} `;
    setText(before + insert + after);
    setMentioned((p) => p.some((x) => x.id === u.id) ? p : [...p, { id: u.id, name: u.name }]);
    setMention(null);
    setTimeout(() => { const el = ref.current; if (el) { const pos = (before + insert).length; el.focus(); el.setSelectionRange(pos, pos); } }, 0);
  };
  const list = mention ? users.filter((u) => u.name && u.name.toLowerCase().includes((mention.query || "").toLowerCase())).slice(0, 6) : [];
  const send = async () => {
    if (!text.trim() || busy) return;
    const mentions = mentioned.filter((m) => text.includes(`@${m.name}`)).map((m) => m.id);
    setBusy(true);
    try { await onSend(text, mentions); setText(""); setMentioned([]); setMention(null); } finally { setBusy(false); }
  };

  return (
    <div className="relative">
      {mention && list.length > 0 && (
        <div data-testid={`${testidPrefix}-mention-dropdown`} className="absolute bottom-full mb-1 left-0 w-56 rounded-xl border border-[#EEEFEF] bg-white shadow-lg py-1 z-20 max-h-52 overflow-y-auto">
          {list.map((u) => (
            <button key={u.id} data-testid={`${testidPrefix}-mention-option-${u.id}`} onMouseDown={(ev) => { ev.preventDefault(); pick(u); }} className="w-full flex items-center gap-2 px-3 py-1.5 hover:bg-[#F6F6FD] text-left text-sm text-[#06060C]">
              <span className="h-6 w-6 rounded-full bg-[#EDE7FF] text-[#5b2fd6] text-[11px] font-semibold flex items-center justify-center shrink-0">{(u.name || "?")[0].toUpperCase()}</span>
              <span className="truncate">{u.name}</span>
            </button>
          ))}
        </div>
      )}
      <div className="flex items-end gap-2">
        <Textarea ref={ref} data-testid={`${testidPrefix}-input`} value={text} onChange={onChange} onKeyDown={(e) => { if (e.key === "Escape") setMention(null); }} disabled={disabled} rows={2} placeholder={placeholder} className="min-h-[38px] max-h-40 bg-[#F6F6FD] border-[#EEEFEF] text-sm resize-y" />
        <Button data-testid={`${testidPrefix}-submit`} onClick={send} disabled={disabled || !text.trim() || busy} className="bg-[#06060C] text-white hover:bg-[#1c1c24] rounded-full shrink-0 h-9 w-9 p-0"><Send className="h-4 w-4" /></Button>
      </div>
    </div>
  );
}
